import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { FC } from 'react';
import { useHealthAndSafetyState } from '../../../../contexts/healthAndSafety.context';

const EmergencyContactsDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedContact } = useHealthAndSafetyState();

  if (!selectedContact) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="emergency-contacts-details-container"
      >
        <Typography>
          {translateText('administration.healthAndSafety.noData')}
        </Typography>
      </Box>
    );
  }

  const getPriorityColor = (priority: number) => {
    if (priority === 1) return 'error';
    if (priority === 2) return 'warning';
    return 'default';
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="emergency-contacts-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText(
          'administration.healthAndSafety.emergencyContactsDetails'
        )}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.contactInformation'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-name">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.contactName'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedContact.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-relationship">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.relationship'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedContact.relationship}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-priority">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.healthAndSafety.columns.priority'
                )}
              </Typography>
              <Chip
                label={
                  selectedContact.priority === 1
                    ? translateText(
                        'administration.healthAndSafety.values.primary'
                      )
                    : `${translateText('administration.healthAndSafety.values.secondary')} (${selectedContact.priority})`
                }
                color={getPriorityColor(selectedContact.priority) as any}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-phone">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.healthAndSafety.columns.phone')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedContact.phoneNumber}
              </Typography>
            </Grid>
            {selectedContact.alternatePhoneNumber && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="contact-alternate-phone"
              >
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.alternatePhone'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedContact.alternatePhoneNumber}
                </Typography>
              </Grid>
            )}
            {selectedContact.email && (
              <Grid item xs={12} sm={6} md={4} data-testid="contact-email">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.columns.email'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedContact.email}
                </Typography>
              </Grid>
            )}
            {selectedContact.address && (
              <Grid item xs={12} data-testid="contact-address">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.address'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedContact.address}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.associatedInformation'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-person-type">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.personType'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedContact.personType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-person-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.personId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedContact.personId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.healthAndSafety.columns.status')}
              </Typography>
              <Chip
                label={
                  selectedContact.isActive
                    ? translateText(
                        'administration.healthAndSafety.status.active'
                      )
                    : translateText(
                        'administration.healthAndSafety.status.inactive'
                      )
                }
                color={selectedContact.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="contact-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.contactId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedContact.id}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmergencyContactsDetailsView;
